import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowClockwise, InstagramLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function RefreshInstagramButton({ campaignId, onDone }) {
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const stopPolling = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setBusy(false);
  };

  const summarize = (data) => {
    if (data.synced > 0) {
      toast.success(
        `IG sync: ${data.synced}/${data.total} refreshed${data.failed ? ` · ${data.failed} failed` : ""}${data.skipped ? ` · ${data.skipped} skipped` : ""}`,
        { duration: 5000 },
      );
    } else if (data.failed > 0) {
      toast.error(
        `IG sync failed for all ${data.failed} creators. First error: ${data.errors?.[0]?.error || data.error || "unknown"}`,
        { duration: 5000 },
      );
    } else if (data.skipped > 0) {
      toast.message(`No Live creators with a reel URL to refresh (${data.skipped} skipped)`, { duration: 5000 });
    } else {
      toast.message("No Live creators to refresh", { duration: 5000 });
    }
  };

  const pollOnce = async () => {
    try {
      const { data } = await api.get(`/instagram/refresh-status`, { params: { campaign_id: campaignId } });
      onDone?.();
      if (data.status && data.status !== "running") {
        summarize(data);
        stopPolling();
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Sync status check failed", { duration: 5000 });
      stopPolling();
    }
  };

  const run = async () => {
    if (!campaignId || busy) return;
    setBusy(true);
    try {
      await api.post(`/instagram/refresh`, { campaign_id: campaignId });
      toast.message("Instagram sync started — refreshing every 3s…", { duration: 5000 });
      pollRef.current = setInterval(pollOnce, 3000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Instagram sync failed to start", { duration: 5000 });
      setBusy(false);
    }
  };

  return (
    <Button
      variant="outline"
      onClick={run}
      disabled={busy}
      className="border-slate-300 h-9 shrink-0"
      data-testid="refresh-ig-btn"
      title="Pull latest metrics from Instagram"
    >
      {busy ? <ArrowClockwise size={14} className="mr-1.5 animate-spin" /> : <InstagramLogo size={14} className="mr-1.5" />}
      {busy ? "Refreshing…" : "Refresh Now"}
    </Button>
  );
}
